import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineOppositeContentProps,
  TimelineProps,
  TimelineSeparator,
} from '@mui/lab'
import { Button, Paper, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { useOrderContext } from '../../contexts/OrderContextProvider'
import BigStatCurrency from '../BigStatCurrency'
import DateDisplay from '../DateDisplay'
import { DetailsTable } from '../DetailsTable'
import { PaymentIcon, QuoteIcon, RfqIcon } from '../icon-catalog'
import AddPOForm from './AddPOForm'
import { AddQuoteForm } from './AddQuoteForm'
import EditPOForm from './EditPOForm'
import { EditQuoteForm } from './EditQuoteForm'
import { EditRfqForm } from './EditRFQForm'
import { Payments } from './Payments'

type Props = {
  timelineProps?: TimelineProps
}
export function OrderTimeline(props: Props) {
  const { timelineProps = {} } = props
  const [editingState, setEditingState] = useState<null | 'rfq' | 'quote' | 'po'>(null)
  const { rfq, refetch, openPaymentDialog } = useOrderContext()
  if (!rfq) {
    return null
  }

  const refresh = () => {
    setEditingState(null)
    refetch()
  }

  const oppContentProps: TimelineOppositeContentProps = {
    align: 'right',
    variant: 'body2',
    color: 'text.secondary',
    sx: { m: 0, mt: 1.75, width: '10em', flex: '0 0 10em' },
  }

  const po = rfq.quote?.purchaseOrder

  return (
    <Timeline {...timelineProps} position="right">
      <TimelineItem>
        <TimelineOppositeContent {...oppContentProps}>
          <DateDisplay date={rfq.dateCreated} absolute />
        </TimelineOppositeContent>
        <TimelineSeparator>
          <TimelineDot>
            <RfqIcon />
          </TimelineDot>
          <TimelineConnector />
        </TimelineSeparator>
        {editingState === 'rfq' ? (
          <TimelineContent sx={{ py: 1.5, px: 2 }}>
            <Typography variant="h6" component="span">
              RFQ
            </Typography>
            <Paper sx={{ p: 2 }} variant="outlined">
              <EditRfqForm rfq={rfq} onUpdated={refresh} onCancel={() => setEditingState(null)} />
            </Paper>
          </TimelineContent>
        ) : (
          <TimelineContent sx={{ py: '12px', px: 2 }}>
            <Stack direction="row" spacing={2}>
              <Typography variant="h6" component="span">
                RFQ
              </Typography>
              <Button variant="outlined" color="primary" size="small" onClick={() => setEditingState('rfq')}>
                Edit
              </Button>
            </Stack>
            <DetailsTable
              items={[
                { key: 'rfqName', label: 'RFQ Name', content: rfq.name },
                {
                  key: 'dateReceived',
                  label: 'Date Received',
                  content: rfq.dateReceived ? <DateDisplay date={rfq.dateReceived} absolute /> : '--',
                },
              ]}
            />
          </TimelineContent>
        )}
      </TimelineItem>
      <TimelineItem>
        <TimelineOppositeContent {...oppContentProps}>
          {rfq.quote && <DateDisplay date={rfq.quote.dateCreated} />}
        </TimelineOppositeContent>
        <TimelineSeparator>
          <TimelineDot>
            <QuoteIcon />
          </TimelineDot>
          <TimelineConnector />
        </TimelineSeparator>
        {rfq.quote ? (
          editingState === 'quote' ? (
            <TimelineContent sx={{ py: 1.5, px: 2 }}>
              <Typography variant="h6" component="span">
                Quote
              </Typography>
              <EditQuoteForm quote={rfq.quote} onUpdated={refresh} onCancel={() => setEditingState(null)} />
            </TimelineContent>
          ) : (
            <TimelineContent sx={{ py: 1.5, px: 2 }}>
              <Stack direction="row" spacing={2}>
                <Typography variant="h6" component="span">
                  Quote
                </Typography>
                <Button variant="outlined" color="primary" size="small" onClick={() => setEditingState('quote')}>
                  Edit
                </Button>
              </Stack>
              <DetailsTable
                items={[
                  { key: 'qno', label: 'Quote Number', content: rfq.quote.quoteNumber },
                  {
                    key: 'amount',
                    label: 'Amount',
                    content: (
                      <BigStatCurrency value={rfq.quote.amount} sx={{ fontSize: '1.75em' }} maximumFractionDigits={2} />
                    ),
                  },
                  {
                    key: 'dateSent',
                    label: 'Date Sent',
                    content: rfq.quote.dateSent && <DateDisplay date={rfq.quote.dateSent} />,
                  },
                  { key: 'designFirm', label: 'Design Firm', content: rfq.quote.designFirm },
                ]}
              />
            </TimelineContent>
          )
        ) : (
          <TimelineContent sx={{ py: 1.5, px: 2 }}>
            <Typography variant="h6" component="span">
              Quote
            </Typography>
            <Typography gutterBottom>Add a quote to this RFQ</Typography>
            <AddQuoteForm rfqId={rfq.id} onQuoteCreated={refresh} />
          </TimelineContent>
        )}
      </TimelineItem>
      {rfq.quote ? (
        <TimelineItem>
          <TimelineOppositeContent {...oppContentProps}>
            {po?.dateCreated && <DateDisplay date={po.dateCreated} />}
          </TimelineOppositeContent>
          <TimelineSeparator>
            <TimelineDot>
              <QuoteIcon />
            </TimelineDot>
            <TimelineConnector />
          </TimelineSeparator>
          {po ? (
            editingState === 'po' ? (
              <TimelineContent sx={{ py: 1.5, px: 2 }}>
                <Typography variant="h6" component="span">
                  Purchase Order
                </Typography>
                <EditPOForm po={po} onPOUpdated={refresh} onCancel={() => setEditingState(null)} />
              </TimelineContent>
            ) : (
              <TimelineContent sx={{ pt: 1.5, px: 2 }}>
                <Stack direction="row" spacing={2}>
                  <Typography variant="h6" component="span">
                    Purchase Order
                  </Typography>
                  <Button variant="outlined" color="primary" size="small" onClick={() => setEditingState('po')}>
                    Edit
                  </Button>
                </Stack>
                <DetailsTable
                  items={[
                    {
                      key: 'amount',
                      label: 'Amount',
                      content: (
                        <BigStatCurrency value={po.amount} sx={{ fontSize: '1.75em' }} maximumFractionDigits={2} />
                      ),
                    },
                    { key: 'poNumber', label: 'PO Number', content: po.poNumber || '--' },
                    { key: 'commission', label: 'Commission', content: `${po.commissionPercentage}%` },
                    { key: 'conf', label: 'Confirmation Number', content: po.confirmationNumber },
                    {
                      key: 'dateEstShip',
                      label: 'Date Estimated Shipment',
                      content: po.dateEstimatedShipment ? <DateDisplay date={po.dateEstimatedShipment} /> : '--',
                    },
                    {
                      key: 'dateReqShip',
                      label: 'Date Requested Shipment',
                      content: po.dateRequestedShipment ? <DateDisplay date={po.dateRequestedShipment} /> : '--',
                    },
                    {
                      key: 'dateRec',
                      label: 'Date Received',
                      content: po.dateReceived ? <DateDisplay date={po.dateReceived} /> : '--',
                    },
                    {
                      key: 'est',
                      label: 'Estimated Income',
                      content: po.commissionPercentage && (
                        <BigStatCurrency
                          value={po.amount * (po.commissionPercentage / 100)}
                          sx={{ fontSize: '1.75em' }}
                          maximumFractionDigits={2}
                          color="success"
                        />
                      ),
                    },
                  ]}
                />
              </TimelineContent>
            )
          ) : (
            <TimelineContent sx={{ py: 1.5, px: 2 }}>
              <Typography variant="h6" component="span">
                Purchase Order
              </Typography>
              <Typography gutterBottom>Add a Purchase Order</Typography>
              <AddPOForm
                quoteId={rfq.quote?.id}
                onPOCreated={refresh}
                defaultCommission={rfq.vendor.commissionPercentage || undefined}
              />
            </TimelineContent>
          )}
        </TimelineItem>
      ) : (
        <TimelineItem>
          <TimelineOppositeContent {...oppContentProps} />
          <TimelineSeparator>
            <TimelineDot>
              <QuoteIcon />
            </TimelineDot>
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent sx={{ py: 1.5, px: 2 }}>
            <Typography variant="h6" component="span">
              Purchase Order
            </Typography>
          </TimelineContent>
        </TimelineItem>
      )}
      <TimelineItem>
        <TimelineOppositeContent {...oppContentProps} />
        <TimelineSeparator>
          <TimelineDot>
            <PaymentIcon />
          </TimelineDot>
        </TimelineSeparator>
        <TimelineContent sx={{ py: 1.5, px: 2 }}>
          <Typography variant="h6" component="span">
            Payments
          </Typography>
          {po && <Payments po={po} onAddPaymentClick={openPaymentDialog} />}
        </TimelineContent>
      </TimelineItem>
    </Timeline>
  )
}
